import { ReactNode, useContext, useEffect, useState } from 'react';
import { KnowledgeBaseContext, KnowledgeBaseContextValue } from 'src/context/KnowledgeBaseContext';
import {
  getInitialTree,
  mergeWithEmptyFolders,
  setVisibilityByTypes,
  sortByDate,
  sortByName,
  sortByType,
} from 'src/utils/fileUtils';
import { getKnowledgeBaseConfig } from 'src/utils/pageStorageConfig';
import { useLoader } from 'src/provider/LoaderProvider';
import { useAppSelector } from 'src/store';

export function useKBContext(): KnowledgeBaseContextValue | null {
  return useContext(KnowledgeBaseContext);
}

export function KnowledgeBaseProvider({ children }: { children: ReactNode }) {
  const [tree, setTree] = useState(getInitialTree());
  const [activeBranch, setActiveBranch] = useState(getInitialTree());
  const [selectedItem, setSelectedItem] = useState(activeBranch);
  const [filteredValue, setFilteredValue] = useState('');
  const [knowledgeBaseName, setKnowledgeBaseName] = useState('Knowledge Base');
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [skipRefetchFiles, setSkipRefetchFiles] = useState(false);
  const { filteredTypes } = useAppSelector((s) => s.knowledgeBaseSlices);
  const loader = useLoader();

  useEffect(() => {
    setSkipRefetchFiles(isEditPopupOpen);
  }, [isEditPopupOpen]);

  const updateActiveBranch = (branch) => {
    loader.show();
    mergeWithEmptyFolders(branch);

    const { sortBy } = getKnowledgeBaseConfig();

    switch (sortBy) {
      case 'type':
        sortByType(branch.children);
        break;
      case 'name':
        sortByName(branch.children);
        sortByType(branch.children);
        break;
      case 'date':
        sortByDate(branch.children);
        sortByType(branch.children);
        break;
      default:
        sortByType(branch.children);
        break;
    }

    setVisibilityByTypes(branch, filteredTypes);
    setActiveBranch(branch);
    loader.hide();
  };

  const value = {
    tree,
    setTree,
    activeBranch,
    updateActiveBranch,
    selectedItem,
    setSelectedItem,
    filteredValue,
    setFilteredValue,
    knowledgeBaseName,
    setKnowledgeBaseName,
    isEditPopupOpen,
    setIsEditPopupOpen,
    skipRefetchFiles,
    setSkipRefetchFiles,
  };

  return <KnowledgeBaseContext.Provider value={value}>{children}</KnowledgeBaseContext.Provider>;
}
